import * as React from 'react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Field, FormSpy } from 'react-final-form';
import { FieldValidator } from 'final-form';
import i18n from 'i18next';
import { subMonths, parseISO, isBefore, isAfter } from 'date-fns';

import SearchFormDatepicker from '../../../../../../SearchFormDatepicker';
import { OnDateChangeHandler } from '../../../../../store/segments/actions';
import { DatesConfig, LocationsConfig } from '../../../../../../types';
import Tooltip from '../../../../../../BaseComponents/Tooltip';
import { useConfig } from '../../../../../../context';
import { useTheme } from '../../../../../../theme';
import { safeStartOfMonth, DayPrice } from '@features/price-graph';
import { getDateWithoutTimezone, getDateWithTimezone } from '../../../../../../Engine/components/FastSearch/utils';
import Value from './Value/Value';
import { PassengerTypeSearchParameter, useGetAvailableDatesQuery } from '@websky/graphql';
import { getMinMaxPrice, isSelectedOnlyDepartureDate } from './utils';
import { useRenderers } from '../../../../../../renderProps';
import { Props as ValueProps } from './Value/Value';
import { useDatepickerMinPrices } from './hooks';
import { useIsMobile } from '../../../../../../utils';

interface Props {
	valueClassName?: string;
	dateTo: Date;
	dateBack?: Date;
	selectedDates: Date[];
	onDateChange: OnDateChangeHandler<void>;
	showPrices?: boolean;
	withDateBack?: boolean;
	clearDates?: (segmentId: number) => void;
	segmentId: number;
	locations: LocationsConfig;
	passengers: PassengerTypeSearchParameter[];

	renderValue?: (props: ValueProps) => React.ReactNode;
	disablePrices?: boolean;
}

export enum DatepickerTab {
	Outbound = 'outbound',
	Return = 'return'
}

export type HoverDates = [Date, Date];

const startDate = new Date();

const validate: FieldValidator<string> = (value, allValues, meta): string | void => {
	if (!meta.data.departure) {
		return i18n.t('SearchForm:Please select departure date') as string;
	}
};

let lastVisibleError = '';

const Datepicker: React.FunctionComponent<Props> = ({
	valueClassName,
	selectedDates,
	onDateChange,
	dateTo,
	dateBack = null,
	withDateBack = false,
	clearDates,
	segmentId,
	locations,
	passengers,
	showPrices,
	renderValue,
	disablePrices
}) => {
	const { Datepicker: css } = useTheme('SearchForm');
	const hasSelectedDates = !!selectedDates.length;

	const [openDate, setOpenDate] = useState(() => {
		if (!withDateBack && selectedDates.length) {
			return selectedDates[selectedDates.length - 1];
		}

		return new Date();
	});

	const [routeType, setRouteType] = useState<DatepickerTab>(dateTo ? DatepickerTab.Return : DatepickerTab.Outbound);
	const [hoverDates, setHoverDates] = useState<HoverDates>([null, null]);
	const [isRouteTypeSetManually, setIsRouteTypeSetManually] = useState(false);
	const [previousManuallyRoute, setPreviousManuallyRoute] = useState<DatepickerTab>(null);
	const [isThisFirstSelectableDate, setIsThisFirstSelectableDate] = useState(false);

	const OverrideSearchFormDatepicker = useRenderers().SearchFormDatePicker ?? SearchFormDatepicker;
	const { SearchFormDatepickerValue: SearchFormDatepickerValueOverride = Value } = useRenderers();

	const isMobile = useIsMobile();

	const {
		SearchForm: { showPriceGraph, separatedDatepicker, useFlightsSchedule }
	} = useConfig();

	const onHover = useCallback(
		(hoverDate: Date) => {
			if (!selectedDates[0] && !selectedDates[1]) {
				setHoverDates([hoverDate, null]);
			}

			if (routeType === DatepickerTab.Outbound) {
				if (isBefore(hoverDate, selectedDates[1])) {
					setHoverDates([hoverDate, selectedDates[1]]);
				} else {
					setHoverDates([null, null]);
				}
			}

			if (routeType === DatepickerTab.Return) {
				if (isAfter(hoverDate, selectedDates[0])) {
					setHoverDates([selectedDates[0], hoverDate]);
				} else {
					setHoverDates([null, null]);
				}
			}
		},
		[selectedDates, isRouteTypeSetManually, routeType, isThisFirstSelectableDate, previousManuallyRoute]
	);

	const { data: scheduleTo } = useGetAvailableDatesQuery({
		skip: !locations.departure || !locations.arrival || (!useFlightsSchedule && (showPrices || showPriceGraph)),
		variables: {
			departure: locations.departure ? locations.departure.iata : '',
			arrival: locations.arrival ? locations.arrival.iata : ''
		}
	});

	const { data: scheduleBack } = useGetAvailableDatesQuery({
		skip:
			!locations.departure ||
			!locations.arrival ||
			(!useFlightsSchedule && (showPrices || showPriceGraph || routeType === 'outbound')),
		variables: {
			departure: locations.arrival ? locations.arrival.iata : '',
			arrival: locations.departure ? locations.departure.iata : ''
		}
	});

	const { legId, isRT } = useMemo(() => {
		const legIdByRoute = routeType === DatepickerTab.Return ? 1 : 0;

		return {
			legId: legIdByRoute,
			isRT: !!dateTo && (!!dateBack || legIdByRoute === 1)
		};
	}, [dateTo, dateBack, routeType]);

	const { minPrices, pricesByLeg, loading } = useDatepickerMinPrices({
		locations,
		passengers,
		legId,
		isRT,
		schedule: useFlightsSchedule ? { to: scheduleTo, back: scheduleBack } : null
	});

	const alternativeHighlighting =
		routeType === 'outbound'
			? scheduleTo && scheduleTo.FlightsSchedule && scheduleTo.FlightsSchedule.flightProbability > 0.5
			: scheduleBack && scheduleBack.FlightsSchedule && scheduleBack.FlightsSchedule.flightProbability > 0.5;

	let highlightingClassName: string;

	if (showPrices) {
		highlightingClassName = css.highlightedDate_withPrice;
	} else {
		highlightingClassName = alternativeHighlighting ? css.highlightedDate_lite : css.highlightedDate;
	}

	const getAvailableDatesFromSchedule = (): Date[] => {
		if (routeType === 'outbound' && scheduleTo && scheduleTo.FlightsSchedule) {
			return scheduleTo.FlightsSchedule.datesData.map(scheduleItem =>
				getDateWithoutTimezone(new Date(scheduleItem.date))
			);
		} else if (routeType === 'return' && scheduleBack && scheduleBack.FlightsSchedule) {
			return scheduleBack.FlightsSchedule.datesData.map(scheduleItem =>
				getDateWithoutTimezone(new Date(scheduleItem.date))
			);
		} else {
			return [];
		}
	};

	const getAvailableDates = () => {
		const availableDates: Date[] = [];

		if (showPrices) {
			if (!pricesByLeg) {
				return [];
			}

			for (const [key, value] of Object.entries<DayPrice>(pricesByLeg)) {
				if (value.price.amount || value.hasFlight) {
					availableDates.push(getDateWithTimezone(new Date(key)));
				}
			}

			return availableDates;
		} else {
			return getAvailableDatesFromSchedule();
		}
	};

	const datesWithMinPrices = useMemo(() => {
		let dates: Date[] = [];

		if (!pricesByLeg) {
			return [];
		}

		const [min, max] = getMinMaxPrice(pricesByLeg);
		const minPrice = min < max ? min + ((max - min) * 20) / 100 : min;

		if (pricesByLeg && minPrice) {
			for (const [key, value] of Object.entries(pricesByLeg)) {
				if (value.price?.amount <= minPrice) {
					dates = [...dates, getDateWithTimezone(new Date(key))];
				}
			}
		}

		return dates;
	}, [routeType, pricesByLeg]);

	const setDate = (date: Date, isBackDate?: boolean): DatesConfig => {
		const config: DatesConfig = {};

		// departure
		if (!isBackDate) {
			if (selectedDates[1] && isAfter(date, selectedDates[1])) {
				config.return = null;
			}

			if (separatedDatepicker && selectedDates[1] && isBefore(date, selectedDates[1])) {
				config.return = selectedDates[1];
			}

			config.departure = date;
		} /*return */ else {
			config.return = date;
			config.departure = selectedDates[0];

			if (selectedDates[0] && isBefore(date, selectedDates[0])) {
				config.departure = date;
				config.return = null;
			}
		}

		if (withDateBack) {
			onDateChange(config.departure, false);
			onDateChange(config.return, true);

			if (!isBackDate && withDateBack && !config.return && !separatedDatepicker) {
				setRouteType(DatepickerTab.Return);
			}
		} else {
			onDateChange(date);
		}

		return config;
	};

	const handlerClear = useCallback(() => {
		setRouteType(DatepickerTab.Outbound);

		clearDates(segmentId);
	}, [clearDates]);

	const clearOpenDate = useCallback(() => {
		setOpenDate(!withDateBack && selectedDates.length ? selectedDates[selectedDates.length - 1] : new Date());
	}, [withDateBack, selectedDates]);

	const onPricesEnd = useCallback(
		(_date: Date, direction: 'next' | 'prev') => {
			if (routeType !== DatepickerTab.Outbound || direction !== 'prev') {
				return;
			}

			const pricesKeys = Object.keys(pricesByLeg);

			if (pricesKeys.length) {
				const lastMonth = pricesKeys[pricesKeys.length - 1];
				const prevMonth = safeStartOfMonth(subMonths(parseISO(lastMonth), 1));
				setOpenDate(prevMonth);
			}
		},
		[routeType, pricesByLeg]
	);

	const handlerClickDatepickerTabOutbound = useCallback(() => {
		setRouteType(DatepickerTab.Outbound);
		setPreviousManuallyRoute(DatepickerTab.Outbound);
		setIsRouteTypeSetManually(true);

		if (isSelectedOnlyDepartureDate(selectedDates)) {
			setIsThisFirstSelectableDate(true);
		}
	}, [selectedDates]);

	const handlerClickDatepickerTabReturn = useCallback(() => {
		if (!selectedDates[0]) {
			return;
		}

		setRouteType(DatepickerTab.Return);
		setPreviousManuallyRoute(DatepickerTab.Return);
		setIsThisFirstSelectableDate(false);
	}, [selectedDates[0]]);

	const handlerCalendarOpen = useCallback(() => {
		if (!separatedDatepicker) {
			setRouteType(DatepickerTab.Outbound);
			setPreviousManuallyRoute(DatepickerTab.Outbound);
		}

		setIsRouteTypeSetManually(true);

		if (isSelectedOnlyDepartureDate(selectedDates) || selectedDates.length === 0) {
			setIsThisFirstSelectableDate(true);
		}
	}, [selectedDates]);

	const isReadyToClose = useCallback(
		(date: Date) => {
			const filterSelectedDates = selectedDates.filter(date => !!date);

			if (filterSelectedDates.length) {
				const isBeforeDate = isBefore(date, filterSelectedDates[0]);

				if (isBeforeDate && !isThisFirstSelectableDate) {
					return false;
				}

				return routeType === DatepickerTab.Return;
			}

			return false;
		},
		[selectedDates, isThisFirstSelectableDate, routeType]
	);

	useEffect(() => {
		if (withDateBack && selectedDates.length === 0 && routeType === DatepickerTab.Return) {
			setRouteType(DatepickerTab.Outbound);
		}
	}, [withDateBack]);

	const isShowPriceGraph =
		!loading && !showPrices && showPriceGraph && !!Object.keys(minPrices.pricesTo || {}).length;

	return (
		<FormSpy
			subscription={{
				submitFailed: true,
				active: true
			}}
		>
			{formState => (
				<Field
					name={`dates_${segmentId}`}
					validate={validate}
					subscription={{
						data: true,
						error: true
					}}
				>
					{fieldState => {
						const onSelect = (date: Date, isBackDate?: boolean) => {
							setHoverDates([null, null]);
							const config = setDate(date, isBackDate);

							setRouteType(DatepickerTab.Return);
							setPreviousManuallyRoute(DatepickerTab.Return);

							setIsThisFirstSelectableDate(false);
							formState.form.mutators.setFieldData(fieldState.input.name, config);
						};

						// We need this for filling out the search form from the outside.
						if ((dateTo || dateBack) && !(fieldState.meta.data as DatesConfig).departure) {
							const config: DatesConfig = {};

							if (dateTo) {
								config.departure = dateTo;
							}

							if (dateBack) {
								config.return = dateBack;
							}

							formState.form.mutators.setFieldData(fieldState.input.name, config);
						}

						if (!dateTo || !dateBack) {
							const config: DatesConfig = {};

							config.departure = dateTo;
							config.return = dateBack;

							formState.form.mutators.setFieldData(fieldState.input.name, {
								departure: dateTo,
								return: dateBack
							});
						}

						if (fieldState.meta.error) {
							lastVisibleError = fieldState.meta.error;
						}

						const handleDatepickerOpen = () => {
							handlerCalendarOpen();
							fieldState.input.onFocus();
						};

						const handleDatepickerClose = () => {
							fieldState.input.onBlur();
						};

						return (
							<Tooltip
								placement={isMobile ? 'bottom' : 'top'}
								title={lastVisibleError}
								open={!!fieldState.meta.error && formState.submitFailed && !formState.active}
							>
								<div className={css.datepicker}>
									<OverrideSearchFormDatepicker
										monthCount={3}
										showProgress={showPrices && loading}
										onSelect={onSelect}
										onHover={withDateBack ? onHover : null}
										hoverDates={hoverDates}
										onClear={handlerClear}
										clearOpenDate={clearOpenDate}
										openDate={openDate}
										selectedDates={selectedDates.filter(date => !!date)}
										minDate={startDate}
										isClearable={hasSelectedDates}
										isDoneable={hasSelectedDates}
										inputClassName={css.input}
										routeType={routeType}
										onOpen={handleDatepickerOpen}
										onClose={handleDatepickerClose}
										onDone={handleDatepickerClose}
										inputFocusedClassName={css.input_focused}
										outsideClickIgnoreClass={`${css.dates.split(' ')[0]}${
											segmentId > 0 ? `_${segmentId}` : ''
										}`}
										showPriceGraph={isShowPriceGraph}
										onPriceGraphPricesEnd={onPricesEnd}
										pricesTo={minPrices.pricesTo}
										pricesBack={minPrices.pricesBack}
										showPriceMatrix={false}
										isDateToSelected={!!dateTo}
										isDateBackSelected={!!dateBack}
										datepickerAlternativeHighlighting={alternativeHighlighting}
										useThemeWithPrices={showPrices}
										highlightedDates={{
											[highlightingClassName]: getAvailableDates(),
											[css.highlighted_minPrice]: datesWithMinPrices
										}}
										closeAfterSelectReturnDate={isReadyToClose}
										onClickOutboundDate={handlerClickDatepickerTabOutbound}
										onClickReturnDate={handlerClickDatepickerTabReturn}
										valueRenderer={(isOpen, open, close) => {
											const valueProps: ValueProps = {
												className: valueClassName,
												dateTo: hoverDates[0] ?? dateTo,
												dateBack: hoverDates[1] ?? dateBack,
												isOpen,
												open,
												close,
												withDateBack,
												routeType,
												setRouteType,
												onClear: handlerClear,
												segmentId,
												setIsRouteTypeSetManually,
												onClickDatepickerTabReturn: handlerClickDatepickerTabReturn,
												onClickDatepickerTabOutbound: handlerClickDatepickerTabOutbound,
												isReturnDateDisabled: !dateTo
											};

											if (renderValue) {
												return renderValue(valueProps);
											}

											return <SearchFormDatepickerValueOverride {...valueProps} />;
										}}
									/>
								</div>
							</Tooltip>
						);
					}}
				</Field>
			)}
		</FormSpy>
	);
};

export default Datepicker;
